import { FC, Fragment } from 'react'
import { Flex } from '@/components/atoms/Grid'
import { Body3, H2, Lead, Tiny } from '@/components/atoms/typography'
import styled, { css } from 'styled-components'
import { Currency, MarketCode, ProductPrices } from '@/network/graphql.g'
import { calculateDiscount, makePriceString } from '@/core/toStringHelper'
import useTranslation from 'next-translate/useTranslation'
import { useDomain } from '@/providers/storeProvider'
import Trans from 'next-translate/Trans'
import { useAsset } from '@/providers/assetsProvider'
import { useUser } from '@/providers/userProvider'

export enum PriceListLayout {
  small,
  big
}

type PriceListProps = {
  prices: ProductPrices
  currency: Currency
  layout?: PriceListLayout
  showClasses?: boolean
  hasSelectedSize?: boolean
  hasStock?: boolean
  hasAllSizesStock?: boolean
  hasSizeInUrl: boolean
  selectedPromoCodePrice?: number
  fromPriceWhenNoSize?: number
  promoCodesCount?: number
} & Rest

const PriceList: FC<PriceListProps> = ({
  prices,
  currency,
  layout = PriceListLayout.small,
  showClasses = false,
  hasSelectedSize,
  hasStock,
  hasAllSizesStock,
  hasSizeInUrl,
  selectedPromoCodePrice,
  fromPriceWhenNoSize,
  promoCodesCount,
  ...rest
}) => {
  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      flexWrap="wrap"
      {...rest}
      data-testid="priceList"
    >
      {makePrices(
        prices,
        layout,
        currency,
        showClasses,
        hasSelectedSize,
        hasStock,
        hasAllSizesStock,
        hasSizeInUrl,
        selectedPromoCodePrice,
        fromPriceWhenNoSize,
        promoCodesCount
      )}
    </Flex>
  )
}

export default PriceList

const makePrices = (
  prices: ProductPrices,
  layout: PriceListLayout,
  currency: Currency,
  showClasses: boolean,
  hasSelectedSize?: boolean,
  hasStock?: boolean,
  hasAllSizesStock?: boolean,
  hasSizeInUrl?: boolean,
  selectedPromoCodePrice?: number,
  fromPriceWhenNoSize?: number,
  promoCodesCount?: number
): JSX.Element => {
  const { finalPrice, minLastMonthPrice, secondPrice, promoCodePrice } =
    prices ?? {}
  const { isEmployee } = useUser()
  const { t } = useTranslation('common')
  const { subdomainName } = useDomain()
  const { useSOIPricing } = useAsset()

  const hasPromoPrice = (promoCodePrice || 0) > 0
  const hasFinalPrice = (finalPrice || 0) > 0
  const hasSecondPrice = (secondPrice || 0) > 0
  const has30DaysPrice = (minLastMonthPrice || 0) > 0

  const discountBestPrice = hasPromoPrice
    ? calculateDiscount(
        (hasSelectedSize ? selectedPromoCodePrice : null) ?? promoCodePrice,
        minLastMonthPrice
      )
    : calculateDiscount(finalPrice, minLastMonthPrice)

  const discountRecommendedPrice = hasPromoPrice
    ? calculateDiscount(
        (hasSelectedSize ? selectedPromoCodePrice : null) ?? promoCodePrice,
        secondPrice
      )
    : calculateDiscount(finalPrice, secondPrice)

  const shouldRemoveDiscount =
    discountBestPrice === 0 || minLastMonthPrice < finalPrice

  const isHungaryCountry = subdomainName === MarketCode.Hu
  const showFromText = hasStock && !hasAllSizesStock

  const soiBasePrice =
    (hasSelectedSize ? selectedPromoCodePrice : null) ??
    promoCodePrice ??
    finalPrice

  switch (layout) {
    case PriceListLayout.small:
      return (
        <>
          {useSOIPricing ? (
            <>
              {hasFinalPrice ? (
                <Flex flexDirection="column">
                  <FirstPriceSmall
                    className={showClasses ? 'final-price' : null}
                  >
                    {(() => {
                      const priceStr = makePriceString({
                        price: soiBasePrice,
                        currency
                      })
                      return !hasSelectedSize && showFromText
                        ? `${t('Information.Postage.From')} ${priceStr}`
                        : priceStr
                    })()}
                  </FirstPriceSmall>
                </Flex>
              ) : null}
              {hasFinalPrice && (
                <Flex flexDirection="column" ml={10}>
                  <SecondPriceSmallNew
                    className={showClasses ? 'previous-price' : null}
                    hasThreePrices={promoCodePrice}
                  >
                    {makePriceString({ price: secondPrice, currency })}
                  </SecondPriceSmallNew>
                </Flex>
              )}
            </>
          ) : (
            <>
              {/*********ProductListing***********SECONDPRICE*********DEFAULT***********/}
              <>
                {hasSecondPrice ? (
                  <SecondPriceSmall
                    className={showClasses ? 'previous-price' : null}
                  >
                    {makePriceString({ price: secondPrice, currency })}
                  </SecondPriceSmall>
                ) : null}
              </>
              {/*********ProductListing***********FINALPRICE*********DEFAULT***********/}
              {hasFinalPrice ? (
                <FinalPriceSmall className={showClasses ? 'final-price' : null}>
                  {(() => {
                    const basePrice =
                      (hasSelectedSize ? selectedPromoCodePrice : null) ??
                      promoCodePrice ??
                      finalPrice
                    const priceStr = makePriceString({
                      price: basePrice,
                      currency
                    })
                    if (isHungaryCountry) {
                      return !hasSelectedSize && showFromText
                        ? `${priceStr}-tól`
                        : priceStr
                    }
                    return !hasSelectedSize && showFromText
                      ? `${t('Information.Postage.From')} ${priceStr}`
                      : priceStr
                  })()}
                </FinalPriceSmall>
              ) : null}
            </>
          )}
        </>
      )

    case PriceListLayout.big:
      return useSOIPricing ? (
        <Flex flexDirection="column">
          {has30DaysPrice ? (
            <RestPricesText>
              <Trans
                i18nKey={
                  shouldRemoveDiscount
                    ? 'common:ProductDetail.PriceList.bestPricePositiveDiscount'
                    : 'common:ProductDetail.PriceList.bestPrice'
                }
                components={[
                  <StyledSpanBestPrice
                    discountBestPrice={discountBestPrice}
                    promoCodePrice={promoCodePrice}
                    minLastMonthPrice={minLastMonthPrice}
                    hasSelectedSize={hasSelectedSize}
                    selectedPromoCodePrice={selectedPromoCodePrice}
                  />
                ]}
                values={{
                  price: makePriceString({
                    price: minLastMonthPrice,
                    currency
                  }),
                  discount: discountBestPrice
                }}
              />
            </RestPricesText>
          ) : null}
          {hasSecondPrice ? (
            <RestPricesText>
              <Trans
                i18nKey="common:ProductDetail.PriceList.recommendedPrice"
                components={[<RecommendedOriginalDiscount />]}
                values={{
                  price: makePriceString({
                    price: secondPrice,
                    currency
                  }),
                  discount: discountRecommendedPrice
                }}
              />
            </RestPricesText>
          ) : null}
        </Flex>
      ) : (
        <Fragment>
          {/*********ProductDetail***********SECONDPRICE*********DEFAULT***********/}
          {hasSecondPrice && (
            <Flex flexDirection="column">
              <SecondPriceBig>
                {makePriceString({ price: secondPrice, currency })}
              </SecondPriceBig>
            </Flex>
          )}
          {/**********ProductDetail**********FINALPRICE*******DEFAULT*************/}
          {isEmployee && hasFinalPrice ? (
            <div className="finalPrice">
              <FirstPriceBig className={showClasses ? 'final-price' : null}>
                {makePriceString({ price: finalPrice, currency })}
              </FirstPriceBig>
            </div>
          ) : null}
          {!isEmployee &&
          (hasFinalPrice || hasPromoPrice) &&
          (!hasSizeInUrl || hasSelectedSize) ? (
            <div className="finalPrice">
              <FirstPriceBig className={showClasses ? 'final-price' : null}>
                {(() => {
                  const basePriceWhenNoSize =
                    fromPriceWhenNoSize ?? promoCodePrice ?? finalPrice
                  const priceStr = !hasSelectedSize
                    ? makePriceString({
                        price: basePriceWhenNoSize,
                        currency
                      })
                    : makePriceString({
                        price:
                          selectedPromoCodePrice ??
                          promoCodePrice ??
                          finalPrice,
                        currency
                      })
                  const shouldShowFromPrefix =
                    !hasSelectedSize && (promoCodesCount ?? 0) >= 2
                  if (isHungaryCountry) {
                    return shouldShowFromPrefix && priceStr
                      ? `${priceStr}-tól`
                      : priceStr
                  }
                  return shouldShowFromPrefix && priceStr
                    ? `${t('Information.Postage.From')} ${priceStr}`
                    : priceStr
                })()}
              </FirstPriceBig>
            </div>
          ) : null}
        </Fragment>
      )
  }
}

const DiscountsStyle = css`
  text-decoration: line-through;
`

const SecondPriceBase = css`
  opacity: ${({ hasThreePrices }) => (hasThreePrices ? 0.4 : 1)};
`

const SecondPriceBig = styled(Body3)`
  ${DiscountsStyle};
  line-height: 30px;
  text-align: ${({ priceTittle }) => (priceTittle ? 'center' : 'left')};
  margin-right: 6px;
  margin-top: 0;
  margin-bottom: 0;
`

const SecondPriceSmall = styled(Tiny)`
  ${DiscountsStyle};
  line-height: 22px;
  text-align: left;
  font-weight: 400;
  order: 3;
  margin: 0;
`

const SecondPriceSmallNew = styled(Tiny)`
  ${SecondPriceBase}
  ${DiscountsStyle}
  line-height: 22px;
  text-align: left;
  margin: 0 10px 0 0;
  font-weight: 400;
  opacity: ${({ hasThreePrices, hasTwoPrices }) =>
    hasThreePrices && !hasTwoPrices && 1};
`

// FirstPrice
const FirstPriceBase = css`
  color: ${(props) => props.theme.colors.navMenuRed};
  text-overflow: ellipsis;
  margin-top: 0;
  margin-bottom: 0;
`

const FirstPriceSmall = styled(Lead)`
  ${FirstPriceBase};
  text-align: left;
`

const FinalPriceSmall = styled(Lead)`
  ${FirstPriceBase};
  order: 1;
`

const FirstPriceBig = styled(H2)`
  ${FirstPriceBase}
  margin-right: 6px;
  margin-left: 8px;
  color: ${(props) => props.theme.colors.accent};
`

const RestPricesText = styled(Tiny)`
  font-weight: 400;
  margin: 0;
  line-height: 1.4;
  color: ${(props) => props.theme.colors.placeholder};
`

const RecommendedOriginalDiscount = styled.span`
  color: ${(props) => props.theme.colors.placeholder};
  font-weight: 400;
  line-height: 2;
`

const StyledSpanBestPrice = styled.span`
  color: ${(props) => props.theme.colors.accent};
  font-weight: 400;
  line-height: 2;
  display: ${(props) =>
    props.discountBestPrice === 0 ||
    props.minLastMonthPrice <
      ((props.hasSelectedSize ? props.selectedPromoCodePrice : null) ??
        props.promoCodePrice)
      ? 'none'
      : 'inline'};
`
